import { REQUIRED, VALIDATIONS } from '../TransportCloudTable.consts';

export interface ValidationResult {
  isValid: boolean;
  errorMessage?: string;
}

export type CellValidator = (
  value: any,
  columnKey: string,
  rowData?: any,
) => ValidationResult;

const validateContainerNumber = (value: any): ValidationResult => {
  if (!value || value === '') {
    return { isValid: true };
  }

  const getCRC = (containerValue: string): number => {
    const map = [
      10, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 23, 24, 25, 26, 27, 28, 29,
      30, 31, 32, 34, 35, 36, 37, 38,
    ];
    const weights = [1, 2, 4, 8, 16, 32, 64, 128, 256, 512];
    const digits: number[] = [];
    let total = 0;
    let check = 0;

    for (let i = 0; i < 4; i++) {
      if (
        containerValue[i] &&
        map[containerValue[i].charCodeAt(0) - 'A'.charCodeAt(0)]
      ) {
        digits[i] = map[containerValue[i].charCodeAt(0) - 'A'.charCodeAt(0)];
      } else {
        return -1;
      }
    }

    for (let i = 4; i < 10; i++) {
      digits[i] = parseInt(containerValue[i], 10);
    }

    for (let i = 0; i < 10; i++) {
      digits[i] *= weights[i];
    }

    for (let i = 0; i < 10; i++) {
      total += digits[i];
    }

    check = total % 11;

    if (check === 10) {
      check = 0;
    }

    return check;
  };

  const isISO6346Valid = (containerValue: string): boolean => {
    if (containerValue.length !== 11) {
      return false;
    }
    return (
      +containerValue[containerValue.length - 1] === getCRC(containerValue)
    );
  };

  if (!isISO6346Valid(value)) {
    return {
      isValid: false,
      errorMessage: VALIDATIONS.validations.containerNumber,
    };
  }

  return { isValid: true };
};

const normalizeDate = (dateValue: any): Date | null => {
  if (
    !dateValue ||
    dateValue === '' ||
    dateValue === null ||
    dateValue === undefined
  ) {
    return null;
  }

  if (typeof dateValue === 'string' && dateValue.includes('-')) {
    const parts = dateValue.split(' ');
    const datePart = parts[0];

    const [day, month, year] = datePart.split('-').map(Number);

    const date = new Date(year, month - 1, day);
    date.setHours(0, 0, 0, 0);

    return date;
  }

  const date = new Date(dateValue);
  date.setHours(0, 0, 0, 0);
  return date;
};

const validateLoadingDeliveryDate = (
  value: any,
  _rowData?: any,
): ValidationResult => {
  if (!value || value === '' || value === null || value === undefined) {
    return {
      isValid: false,
      errorMessage: REQUIRED.loadingDeliveryDate,
    };
  }

  return { isValid: true };
};

const validatePickupDate = (value: any, rowData?: any): ValidationResult => {
  const tradeType = rowData?.tradeType;

  if (tradeType === 'IMPORT') {
    if (!value || value === '' || value === null || value === undefined) {
      return {
        isValid: false,
        errorMessage: REQUIRED.pickupDate,
      };
    }
  }

  if (!value || value === '' || value === null || value === undefined) {
    return { isValid: true };
  }

  if (!rowData) {
    return { isValid: true };
  }

  const pickupDate = normalizeDate(value);
  const loadingDeliveryDate = normalizeDate(rowData?.loadingDeliveryDate);
  const deliveryDate = normalizeDate(rowData?.deliveryDate);

  if (!pickupDate || isNaN(pickupDate.getTime())) {
    return { isValid: true };
  }

  if (
    loadingDeliveryDate &&
    !isNaN(loadingDeliveryDate.getTime()) &&
    pickupDate > loadingDeliveryDate
  ) {
    return {
      isValid: false,
      errorMessage: VALIDATIONS.validations.pickupDate,
    };
  }

  if (
    deliveryDate &&
    !isNaN(deliveryDate.getTime()) &&
    pickupDate > deliveryDate
  ) {
    return {
      isValid: false,
      errorMessage: VALIDATIONS.validations.pickupDate,
    };
  }

  return { isValid: true };
};

const validateDeliveryDate = (value: any, rowData?: any): ValidationResult => {
  const tradeType = rowData?.tradeType;

  if (tradeType === 'EXPORT') {
    if (!value || value === '' || value === null || value === undefined) {
      return {
        isValid: false,
        errorMessage: REQUIRED.deliveryDate,
      };
    }
  }

  if (!value || value === '' || value === null || value === undefined) {
    return { isValid: true };
  }

  if (!rowData) {
    return { isValid: true };
  }

  const deliveryDate = normalizeDate(value);
  const pickupDate = normalizeDate(rowData?.pickupDate);
  const loadingDeliveryDate = normalizeDate(rowData?.loadingDeliveryDate);

  if (!deliveryDate || isNaN(deliveryDate.getTime())) {
    return { isValid: true };
  }

  if (pickupDate && !isNaN(pickupDate.getTime()) && deliveryDate < pickupDate) {
    return {
      isValid: false,
      errorMessage: VALIDATIONS.validations.deliveryDate,
    };
  }

  if (
    loadingDeliveryDate &&
    !isNaN(loadingDeliveryDate.getTime()) &&
    deliveryDate < loadingDeliveryDate
  ) {
    return {
      isValid: false,
      errorMessage: VALIDATIONS.validations.deliveryDate,
    };
  }

  return { isValid: true };
};

const validateShippingLine = (value: any): ValidationResult => {
  if (!value || value === '' || value === null || value === undefined) {
    return {
      isValid: false,
      errorMessage: VALIDATIONS.validations.shippingLine,
    };
  }

  return { isValid: true };
};

const validateContainerType = (value: any): ValidationResult => {
  if (!value || value === '' || value === null || value === undefined) {
    return {
      isValid: false,
      errorMessage: VALIDATIONS.validations.containerType,
    };
  }

  return { isValid: true };
};

const validateTruckerRate = (value: any): ValidationResult => {
  if (!value || value === '' || value === null || value === undefined) {
    return { isValid: true };
  }

  if (typeof value === 'object') {
    const rate = value.rate || value.amount;
    const currency = value.currency;

    const hasRate = rate !== null && rate !== undefined && rate !== '';
    const hasCurrency = currency !== null && currency !== undefined;

    if (hasRate) {
      const rateNumber = parseFloat(rate);
      if (!isNaN(rateNumber) && rateNumber < 0) {
        return {
          isValid: false,
          errorMessage: VALIDATIONS.validations.truckerRateNegative,
        };
      }
    }

    if (hasRate && !hasCurrency) {
      return {
        isValid: false,
        errorMessage: VALIDATIONS.validations.truckerRateValue,
      };
    }

    if (hasCurrency && !hasRate) {
      return {
        isValid: false,
        errorMessage: VALIDATIONS.validations.truckerRateCurrency,
      };
    }

    return { isValid: true };
  }

  const stringValue = String(value).trim();

  if (stringValue === '') {
    return { isValid: true };
  }

  const parts = stringValue.split(' ');

  const ratePart = parts[0];
  const rateValue = ratePart ? parseFloat(ratePart) : NaN;

  const hasRate = !isNaN(rateValue) && ratePart !== '';

  if (hasRate && rateValue < 0) {
    return {
      isValid: false,
      errorMessage: VALIDATIONS.validations.truckerRateNegative,
    };
  }

  const currencyPart = parts.slice(1).join(' ').trim();
  const hasCurrency = currencyPart !== '';

  if (!hasRate && stringValue !== '') {
    return {
      isValid: false,
      errorMessage: VALIDATIONS.validations.truckerRateCurrency,
    };
  }

  if (hasRate && !hasCurrency) {
    return {
      isValid: false,
      errorMessage: VALIDATIONS.validations.truckerRateValue,
    };
  }

  return { isValid: true };
};

const validateReferenceNumbers = (value: any): ValidationResult => {
  if (!value || value === '' || value === null || value === undefined) {
    return { isValid: true };
  }

  const stringValue = String(value).trim();
  if (stringValue === '') return { isValid: true };

  const parts = stringValue.split(/[/,]/);

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i].trim();
    if (part === '' || part === '-') continue;

    const isJobNumber = i === 0;
    const minLength = isJobNumber ? 0 : 2;

    if (part.length < minLength || part.length > 64) {
      return {
        isValid: false,
        errorMessage: VALIDATIONS.validations.referenceNumbers,
      };
    }
  }

  return { isValid: true };
};

const validateLoadingDeliveryLocation = (
  value: any,
  _rowData?: any,
): ValidationResult => {
  if (!value || value === '' || value === null || value === undefined) {
    return {
      isValid: false,
      errorMessage: REQUIRED.loadingDeliveryLocation,
    };
  }

  return { isValid: true };
};

const validatePickupLocation = (
  value: any,
  rowData?: any,
): ValidationResult => {
  const tradeType = rowData?.tradeType;

  if (tradeType === 'IMPORT') {
    if (!value || value === '' || value === null || value === undefined) {
      return {
        isValid: false,
        errorMessage: REQUIRED.pickupLocation,
      };
    }
  }

  return { isValid: true };
};

const validateDeliveryLocation = (
  value: any,
  rowData?: any,
): ValidationResult => {
  const tradeType = rowData?.tradeType;

  if (tradeType === 'EXPORT') {
    if (!value || value === '' || value === null || value === undefined) {
      return {
        isValid: false,
        errorMessage: REQUIRED.deliveryLocation,
      };
    }
  }

  return { isValid: true };
};

const validateStringLength = (
  value: any,
  maxLength: number,
  errorMessage: string,
  minLength: number = 0,
): ValidationResult => {
  if (!value || value === '' || value === null || value === undefined) {
    return { isValid: true };
  }

  const stringValue = String(value);

  if (stringValue.length < minLength || stringValue.length > maxLength) {
    return {
      isValid: false,
      errorMessage,
    };
  }

  return { isValid: true };
};

export const validateCell = (
  value: any,
  columnKey: string,
  rowData?: any,
): ValidationResult => {
  switch (columnKey) {
    case 'containerNumber':
      return validateContainerNumber(value);
    case 'loadingDeliveryDate':
      return validateLoadingDeliveryDate(value, rowData);
    case 'pickupDate':
      return validatePickupDate(value, rowData);
    case 'deliveryDate':
      return validateDeliveryDate(value, rowData);
    case 'shippingLine':
      return validateShippingLine(value);
    case 'containerType':
      return validateContainerType(value);
    case 'truckerRate':
      return validateTruckerRate(value);
    case 'referenceNumbers':
      return validateReferenceNumbers(value);
    case 'pickupLocation':
      return validatePickupLocation(value, rowData);
    case 'deliveryLocation':
      return validateDeliveryLocation(value, rowData);
    case 'comment':
      return validateStringLength(value, 256, VALIDATIONS.validations.comment);
    case 'loadingDeliveryLocation':
      return validateLoadingDeliveryLocation(value, rowData);
    case 'bookingNumber':
      return validateStringLength(
        value,
        64,
        VALIDATIONS.validations.bookingNumber,
      );
    case 'driverName':
      return validateStringLength(
        value,
        40,
        VALIDATIONS.validations.driverName,
      );
    case 'driverDocumentNumber':
      return validateStringLength(
        value,
        40,
        VALIDATIONS.validations.driverDocumentNumber,
      );
    case 'driverPhone':
      return validateStringLength(
        value,
        20,
        VALIDATIONS.validations.driverPhone,
      );
    case 'vehicleRegistrationNumber':
      return validateStringLength(
        value,
        40,
        VALIDATIONS.validations.vehicleRegistrationNumber,
      );
    case 'trailerRegistrationNumber':
      return validateStringLength(
        value,
        40,
        VALIDATIONS.validations.trailerRegistrationNumber,
      );
    case 'reportNotes':
      return validateStringLength(
        value,
        256,
        VALIDATIONS.validations.reportNotes,
      );
    case 'loadDescription':
      return validateStringLength(
        value,
        256,
        VALIDATIONS.validations.loadDescription,
      );
    case 'pinCode':
      return validateStringLength(
        value,
        64,
        VALIDATIONS.validations.pinCode,
        2,
      );
    default:
      return { isValid: true };
  }
};

export const hasValidation = (columnKey: string): boolean => {
  const columnsWithValidation = [
    'containerNumber',
    'loadingDeliveryDate',
    'pickupDate',
    'deliveryDate',
    'shippingLine',
    'containerType',
    'truckerRate',
    'referenceNumbers',
    'pickupLocation',
    'deliveryLocation',
    'comment',
    'loadingDeliveryLocation',
    'bookingNumber',
    'driverName',
    'driverDocumentNumber',
    'driverPhone',
    'vehicleRegistrationNumber',
    'trailerRegistrationNumber',
    'reportNotes',
    'loadDescription',
    'pinCode',
  ];
  return columnsWithValidation.includes(columnKey);
};
